/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Formik } from 'formik';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import Container from '@material-ui/core/Container';
import Autocomplete from '@material-ui/lab/Autocomplete';
import userFormSchema from './userFormSchema';
import { createUser, cancelCreateUser, userEmailChange } from '../../actions/users';
import { selectCreateUserError, selectSubmittingUser } from '../../selectors/users';
import { ClientLobSelection } from '../../components/LineOfBusinessSelector/ClientLobSelection';

export const LINES_OF_BUSINESS_FIELD = 'businessLines';

const useStyles = makeStyles(theme => ({
  form: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  selectRow: {
    display: 'flex',
    marginLeft: theme.spacing(1),
  },
  nameRow: {
    display: 'flex',
    marginTop: theme.spacing(1),
  },
  title: {
    marginBottom: theme.spacing(3),
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  paper: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  button: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(1),
  },
  checkboxRow: {
    padding: theme.spacing(1),
    display: 'flex',
    alignItems: 'end',
  },
  clientsWrapper: {
    marginTop: theme.spacing(3),
    paddingBottom: theme.spacing(2.5),
  },
  clientsInput: {
    minWidth: 200,
  },
}));

export const AdminUserForm = ({
  onCreateUser,
  onCancelCreateUser,
  onEmailChange,
  createUserError,
  appMeta,
  epiAccess,
  availableClients,
  isSubmitting,
}) => {
  const initialValues = {
    email: '',
    name: '',
    clients: [],
    [LINES_OF_BUSINESS_FIELD]: [],
    isAdmin: false,
    isInternal: false,
  };
  const selectClients = epiAccess.isInternal
    ? availableClients
    : availableClients.filter(c => appMeta.clientIds.includes(c.id));
  const { message: creationError } = createUserError;
  const classes = useStyles();

  const [selectedUser, setSelectedUser] = useState({
    clients: selectClients.filter(({ id }) => initialValues.clients.includes(id)),
  });
  const [clientIds, setClientIds] = useState([]);

  return (
    <>
      <Paper className={classes.paper}>
        <Formik
          initialValues={initialValues}
          onSubmit={onCreateUser}
          validationSchema={userFormSchema}
        >
          {formikProps => {
            const {
              values,
              touched,
              errors,
              setFieldValue,
              handleChange,
              handleBlur,
              handleSubmit,
            } = formikProps;
            const error = creationError || (touched.email && errors.email);
            return (
              <form autoComplete="off" onSubmit={handleSubmit}>
                <div className={classes.form}>
                  <Typography variant="h3" className={classes.title}>
                    Create User
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={12}>
                      <TextField
                        required
                        error={!!error}
                        className={classes.textField}
                        label="User Email"
                        name="email"
                        value={values.email}
                        onChange={data => {
                          onEmailChange(data);
                          return handleChange(data);
                        }}
                        onBlur={handleBlur}
                        variant="outlined"
                        helperText={error || ''}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </div>
                <div className={classes.nameRow}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={12}>
                      <TextField
                        required
                        error={errors.name ? touched.name : null}
                        className={classes.textField}
                        label="Name"
                        name="name"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        variant="outlined"
                        helperText={errors.name && touched.name ? errors.name : ''}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </div>
                <div className={classes.buttonRow}>
                  <div className={classes.selectRow}>
                    <Container className={classes.clientsWrapper} maxwidth="lg">
                      <FormControl>
                        <Autocomplete
                          multiple
                          id="simple-select"
                          name="clients"
                          value={values.clients.id}
                          options={selectClients.filter(sc => !clientIds.includes(sc.id))}
                          disableCloseOnSelect
                          onChange={(event, value) => {
                            // only fix to make formiks work with Select
                            // eslint-disable-next-line no-param-reassign
                            event.target.name = 'clients';
                            setFieldValue(
                              'clients',
                              value.map(val => val.id)
                            );
                            setClientIds(value.map(val => val.id));
                            setSelectedUser({
                              ...selectedUser,
                              clients: selectClients.filter(({ id }) => value.includes(id)),
                            });
                          }}
                          getOptionLabel={option => option.name}
                          renderOption={option => (
                            <Typography variant="subtitle2">{option.name}</Typography>
                          )}
                          renderInput={params => (
                            <TextField
                              {...params}
                              style={{ width: 300 }}
                              variant="outlined"
                              label="Clients"
                            />
                          )}
                        />
                      </FormControl>
                    </Container>
                  </div>
                  <ul>
                    <ClientLobSelection
                      clients={selectClients.filter(({ id }) => clientIds.includes(id))}
                      onChange={linesOfBusiness =>
                        setFieldValue(LINES_OF_BUSINESS_FIELD, linesOfBusiness)
                      }
                    />
                  </ul>
                </div>

                <div className={classes.buttonRow}>
                  <div className={classes.checkboxRow}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="isAdmin"
                          checked={values.isAdmin}
                          onChange={handleChange}
                          color="primary"
                        />
                      }
                      label="Admin"
                    />
                    {epiAccess.isInternal ? (
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="isInternal"
                            checked={values.isInternal}
                            onChange={handleChange}
                            color="primary"
                          />
                        }
                        label="Internal"
                      />
                    ) : (
                      <></>
                    )}
                  </div>

                  <div>
                    <Button
                      onClick={onCancelCreateUser}
                      className={classes.button}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      className={classes.button}
                      disabled={isSubmitting}
                    >
                      Create
                    </Button>
                  </div>
                </div>
              </form>
            );
          }}
        </Formik>
      </Paper>
    </>
  );
};

AdminUserForm.defaultProps = {
  onCreateUser: () => {},
  onCancelCreateUser: () => {},
  onEmailChange: () => {},
  isSubmitting: false,
  appMeta: {},
  epiAccess: {},
  availableClients: {},
  createUserError: {},
};

AdminUserForm.propTypes = {
  onCreateUser: PropTypes.func,
  onCancelCreateUser: PropTypes.func,
  onEmailChange: PropTypes.func,
  createUserError: PropTypes.shape({
    message: PropTypes.string,
    status: PropTypes.number,
  }),
  isSubmitting: PropTypes.bool,
  appMeta: PropTypes.shape({
    auth0UserId: PropTypes.string,
    clientIds: PropTypes.arrayOf(PropTypes.number),
  }),
  epiAccess: PropTypes.shape({
    isInternal: PropTypes.bool,
    isAdmin: PropTypes.bool,
  }),
  availableClients: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      billingAddressLine1: PropTypes.string,
      billingAddressLine2: PropTypes.string,
      billingAddressZip: PropTypes.string,
      billingAddressState: PropTypes.string,
      billingAddressCity: PropTypes.string,
    })
  ),
};

const mapStateToProps = state => {
  const { auth, clients } = state;
  return {
    createUserError: selectCreateUserError(state),
    appMeta: auth.profile[process.env.REACT_APP_APP_METADATA_KEY],
    epiAccess: auth.profile[process.env.REACT_APP_APP_METADATA_KEY].episourceAccess,
    availableClients: clients.data,
    isSubmitting: selectSubmittingUser(state),
  };
};

export function mapDispatchToProps(dispatch) {
  return {
    onCreateUser: params => dispatch(createUser(params)),
    onCancelCreateUser: () => dispatch(cancelCreateUser()),
    onEmailChange: data => dispatch(userEmailChange(data)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(AdminUserForm);
